import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { TreeView } from "@mui/x-tree-view";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTreeViewContext } from "./TreeViewContext";
import { TreeHeader } from "./components/TreeHeader";
import { PlantNode } from "./components/PlantNode";
import { ConfirmNavigationDialog } from "./components/ConfirmNavigationDialog";
import { PlantDialogs } from "./components/PlantDialogs";
import { WbsDialogs } from "./components/WbsDialogs";
import { treeViewSx } from "./styles/treeViewSx";

/**
 * Presentational Plant -> WBS -> Documents/Input Data/Estimation tree.
 *
 * Orchestrator only: data and operations live in TreeViewContext (see
 * TreeViewContext.jsx), every row and dialog below is its own component.
 * Shared by the global AppSidebar's ProjectsTreeSection and the full-page
 * EmsExplorer so both look and behave identically for search/expand/load.
 */
const TreeViewMain = () => {
  const {
    treeData,
    pickerMode,
    listOpen,
    visiblePlants,
    nodeTargetRef,
    handleNodeSelect,
  } = useTreeViewContext();
  const { plantsLoading, query, expanded, handleNodeToggle } = treeData;

  // Targets are rebuilt on every render but never cleared (see
  // RevisionDocNode for the one case that deletes its own), so reset the
  // registry here - the rows below re-register as they render.
  nodeTargetRef.current = {};

  return (
    <>
      <TreeHeader />

      {!listOpen ? null : plantsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : visiblePlants.length === 0 ? (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {query ? "No matches." : "No projects available."}
          </Typography>
        </Box>
      ) : (
        <TreeView
          expanded={expanded}
          onNodeToggle={handleNodeToggle}
          onNodeSelect={handleNodeSelect}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          sx={treeViewSx}
        >
          {visiblePlants.map((plant) => (
            <PlantNode key={plant.id} plant={plant} />
          ))}
        </TreeView>
      )}

      {!pickerMode && (
        <>
          <ConfirmNavigationDialog />
          <PlantDialogs />
          <WbsDialogs />
        </>
      )}
    </>
  );
};

export default TreeViewMain;
